import { loadStripe, Stripe } from '@stripe/stripe-js';
import { sentry } from './sentry';
import config from 'config';

/**
 * Type guard to ensure error values are Error instances
 * Converts unknown error types to proper Error objects for safe handling
 */
function ensureError(value: unknown): Error {
  if (value instanceof Error) return value;
  return new Error(String(value));
}

/**
 * Loads Stripe.js with retry logic to handle transient failures
 * 
 * This function attempts to load Stripe.js up to 3 times with 2-second delays
 * between attempts. This helps handle:
 * - Slow network connections
 * - Temporary CDN issues
 * - Race conditions during page load
 * 
 * @param publishableKey - Stripe publishable key
 * @param retryCount - Current retry attempt (internal use)
 * @returns Promise that resolves to Stripe instance
 * @throws Error if all retry attempts fail
 * 
 * @example
 * ```typescript
 * const stripePromise = loadStripeWithRetry(config.stripe.PUBLIC_KEY);
 * ```
 */
export const loadStripeWithRetry = async (
  publishableKey: string,
  retryCount = 0,
): Promise<Stripe | null> => {
  try {
    const stripe = await loadStripe(publishableKey);
    
    if (!stripe) {
      throw new Error('Failed to load Stripe.js');
    }
    
    return stripe;
  } catch (error) {
    console.error(
      `Stripe.js load failed (attempt ${retryCount + 1}/3):`,
      error,
    );

    // Retry up to 2 more times (3 total attempts)
    if (retryCount < 2) {
      console.log(`Retrying Stripe.js load in 2 seconds...`);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return loadStripeWithRetry(publishableKey, retryCount + 1);
    }

    // Final failure after 3 attempts
    console.error('Stripe.js failed to load after 3 attempts');
    sentry.captureException(ensureError(error), {
      tags: {
        component: 'stripeLoader',
        retryCount: retryCount + 1,
      },
    });
    
    throw error;
  }
};

/**
 * Pre-configured Stripe promise instance for use throughout the application
 * This avoids circular dependencies by keeping Stripe initialization separate from app bootstrap
 */
export const stripePromise = (() => {
  const publicKey = config.stripe.PUBLIC_KEY;
  if (!publicKey) {
    console.error('Stripe public key is not configured');
    return Promise.resolve(null);
  }
  return loadStripeWithRetry(publicKey);
})();
